import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { getCountriesByRegion, getCountryBySlug, CountryData } from '../data/countryData';
import { applySeo, breadcrumbJsonLd } from '../seo/tags';
import {
  PageCta,
  PageHero,
  SectionHeading,
} from '../components/page-templates/SitePageTemplates';

const CountryFlag: React.FC<{
  code: string;
  size?: 'sm' | 'md' | 'lg';
}> = ({ code, size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-5 w-7',
    md: 'h-8 w-12',
    lg: 'h-12 w-16',
  };

  return (
    <img
      src={`https://flagcdn.com/w160/${code.toLowerCase()}.png`}
      alt={`${code} flag`}
      className={`${sizeClasses[size]} rounded object-cover shadow-sm`}
    />
  );
};

const RelatedCountryCard: React.FC<{ country: CountryData }> = ({ country }) => (
  <Link
    to={`/countries/${country.slug}`}
    className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white px-4 py-3 transition-colors hover:border-cyan-200"
  >
    <CountryFlag code={country.code} size="sm" />
    <div>
      <p className="text-sm font-medium text-slate-950">{country.name}</p>
      <p className="text-xs text-slate-500">{country.region}</p>
    </div>
  </Link>
);

const CountryDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const country = slug ? getCountryBySlug(slug) : undefined;

  useEffect(() => {
    if (!country) {
      applySeo({ title: 'Country not found | TrueLog', noindex: true });
      return;
    }
    applySeo({
      title: `${country.name} ICT Logistics & IOR/EOR Services | TrueLog`,
      description: `${country.tagline} ${country.description}`,
      path: `/countries/${country.slug}`,
      jsonLd: breadcrumbJsonLd([
        { name: 'Home', path: '/' },
        { name: 'Global coverage', path: '/global-coverage' },
        { name: country.name, path: `/countries/${country.slug}` },
      ]),
    });
  }, [country]);

  if (!country) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 pt-32 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl rounded-[34px] border border-slate-200/80 bg-white p-10 text-center shadow-[0_28px_70px_-42px_rgba(15,23,42,0.22)]">
          <h1 className="text-4xl font-bold tracking-tight text-slate-950">Country not found</h1>
          <p className="mt-4 text-sm leading-7 text-slate-600">
            The market you are looking for is not available in the current coverage dataset.
          </p>
          <Link
            to="/global-coverage"
            className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-[#0E9ED5] px-6 py-3 text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#0b7aa6]"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back to coverage
          </Link>
        </div>
      </div>
    );
  }

  const relatedCountries = getCountriesByRegion(country.region)
    .filter((item) => item.code !== country.code)
    .slice(0, 6);

  return (
    <div className="bg-white">
      <PageHero
        eyebrow={`${country.region} Market`}
        title={`${country.name} market detail`}
        description={country.description}
        heroIcon={GlobeAltIcon}
        accentIcon={ShieldCheckIcon}
        actions={[
          { href: '/contact-us', label: `Plan for ${country.name}` },
          { href: '/global-coverage', label: 'Back to coverage', variant: 'secondary' },
        ]}
        badges={country.keyIndustries.slice(0, 3)}
        metrics={[
          { value: country.metrics.taxRate, label: 'Tax rate' },
          { value: country.metrics.dutyRate, label: 'Duty rate' },
          { value: country.metrics.leadTime, label: 'Lead time' },
          {
            value: country.hasOffice ? 'Office' : country.isPartner ? 'Partner' : 'Coverage',
            label: 'TrueLog presence',
          },
        ]}
        panelEyebrow="Country snapshot"
        panelTitle={country.tagline}
        panelDescription={`Capital: ${country.capital} • Population: ${country.population} • Currency: ${country.metrics.currency}`}
        panelItems={country.compliance.slice(0, 3).map((item) => ({
          icon: ShieldCheckIcon,
          title: item.title,
          description: item.description,
        }))}
      />

      <section className="bg-white py-20">
        <div className="container-custom grid gap-8 lg:grid-cols-[1.02fr_0.98fr]">
          <div>
            <SectionHeading
              eyebrow="Country Profile"
              title="Commercial and operational context"
              description="Use this profile as a starting point for understanding how imports, services, and local execution may need to be structured in this market."
              align="left"
            />

            <div className="rounded-[30px] border border-slate-200/80 bg-white p-7 shadow-[0_24px_60px_-40px_rgba(15,23,42,0.28)]">
              <div className="flex items-start gap-5">
                <CountryFlag code={country.code} size="lg" />
                <div>
                  <h2 className="text-3xl font-bold tracking-tight text-slate-950">{country.name}</h2>
                  <p className="mt-2 text-sm leading-7 text-slate-600">{country.tagline}</p>
                </div>
              </div>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Capital</p>
                  <p className="mt-2 text-sm text-slate-700">{country.capital}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Population</p>
                  <p className="mt-2 text-sm text-slate-700">{country.population}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Timezone</p>
                  <p className="mt-2 text-sm text-slate-700">{country.metrics.timezone}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Languages</p>
                  <p className="mt-2 text-sm text-slate-700">{country.metrics.language.join(', ')}</p>
                </div>
              </div>

              {country.hasOffice && country.officeLocation ? (
                <div className="mt-6 rounded-[24px] border border-cyan-100 bg-cyan-50 px-5 py-5">
                  <div className="flex items-center gap-3 text-cyan-700">
                    <BuildingOffice2Icon className="h-5 w-5" />
                    <p className="text-sm font-medium">TrueLog office: {country.officeLocation}</p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <SectionHeading
                eyebrow="Services"
                title={`How TrueLog can support ${country.name}`}
                description="These are the service areas typically relevant when shipping, deploying, or recovering ICT hardware in this market."
                align="left"
              />

              <div className="rounded-[30px] border border-slate-200/80 bg-slate-50 p-6 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.22)]">
                <div className="space-y-3">
                  {country.services.map((service) => (
                    <div key={service} className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3">
                      <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#0E9ED5]" />
                      <p className="text-sm leading-7 text-slate-600">{service}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <SectionHeading
                eyebrow="Challenges"
                title="Typical trade friction in this market"
                description="These pain points usually shape how quickly hardware can enter, clear, and reach site."
                align="left"
              />

              <div className="rounded-[30px] border border-slate-900/20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-7 text-white shadow-[0_28px_70px_-42px_rgba(15,23,42,0.85)]">
                <div className="space-y-3">
                  {country.painPoints.map((point) => (
                    <div key={point} className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.06] px-4 py-4">
                      <TruckIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-cyan-300" />
                      <p className="text-sm leading-7 text-slate-200">{point}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container-custom grid gap-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <SectionHeading
              eyebrow="Compliance"
              title="Requirements that usually need attention first"
              description="These are the country-specific checkpoints that shape compliant import execution and should be addressed before shipment movement is locked in."
              align="left"
            />

            <div className="space-y-4">
              {country.compliance.map((item) => (
                <div
                  key={item.title}
                  className="rounded-[26px] border border-slate-200/80 bg-white p-5 shadow-[0_20px_50px_-40px_rgba(15,23,42,0.22)]"
                >
                  <h3 className="text-lg font-semibold text-slate-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-7 text-slate-600">{item.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <SectionHeading
                eyebrow="Industries"
                title="Common sectors in market"
                description="These industries often drive the demand profile and regulatory pressure for ICT movement into this country."
                align="left"
              />
              <div className="flex flex-wrap gap-2">
                {country.keyIndustries.map((industry) => (
                  <span key={industry} className="rounded-full bg-white px-4 py-2 text-sm text-slate-600 shadow-[0_16px_35px_-28px_rgba(15,23,42,0.25)]">
                    {industry}
                  </span>
                ))}
              </div>
            </div>

            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-[28px] border border-rose-200 bg-rose-50 p-6">
                <div className="flex items-center gap-2 text-rose-700">
                  <ExclamationTriangleIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">Prohibited items</h3>
                </div>
                <ul className="mt-4 space-y-3">
                  {country.prohibitedItems.map((item) => (
                    <li key={item} className="text-sm leading-7 text-rose-700">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-[28px] border border-amber-200 bg-amber-50 p-6">
                <div className="flex items-center gap-2 text-amber-700">
                  <ExclamationTriangleIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold">Restricted items</h3>
                </div>
                <ul className="mt-4 space-y-3">
                  {country.restrictedItems.map((item) => (
                    <li key={item} className="text-sm leading-7 text-amber-700">
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div>
              <SectionHeading
                eyebrow="Trade Agreements"
                title="Relevant agreement landscape"
                description="Trade agreements can influence import treatment and should be assessed alongside product classification and local requirements."
                align="left"
              />
              <div className="flex flex-wrap gap-2">
                {country.tradeAgreements.map((agreement) => (
                  <span key={agreement} className="rounded-full bg-white px-4 py-2 text-sm text-slate-600 shadow-[0_16px_35px_-28px_rgba(15,23,42,0.25)]">
                    {agreement}
                  </span>
                ))}
              </div>
            </div>

            {relatedCountries.length ? (
              <div>
                <SectionHeading
                  eyebrow="Nearby Markets"
                  title={`Other countries in ${country.region}`}
                  description="If your rollout spans the wider region, compare nearby markets before finalising the operating model."
                  align="left"
                />
                <div className="space-y-3">
                  {relatedCountries.map((relatedCountry) => (
                    <RelatedCountryCard key={relatedCountry.code} country={relatedCountry} />
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      <PageCta
        title={`Need to plan entry into ${country.name} with less risk?`}
        description={`Share the equipment type, delivery window, and compliance context for ${country.name}. We can help shape the right import and execution model before hardware moves.`}
        pills={[country.name, ...country.keyIndustries.slice(0, 2)]}
        secondaryHref="/global-coverage"
        secondaryLabel="Back to coverage"
      />
    </div>
  );
};

export default CountryDetail;
